<template>
  <div v-if="layout === 'row'" data-test-id="steps-description-row">
    <div v-if="title || subtitle" class="mb-4">
      <h2
        v-if="title"
        class="text-h6 font-semibold text-stone-800"
        data-test-id="steps-description-row-title"
      >
        {{ title }}
      </h2>
      <p
        v-if="subtitle"
        class="mt-1 text-sm text-stone-500"
        data-test-id="steps-description-row-subtitle"
      >
        {{ subtitle }}
      </p>
    </div>
    <div
      class="divide-y divide-gray-200 border-t border-b border-gray-200 md:grid md:grid-cols-2 xl:grid-cols-4 md:divide-y-0"
    >
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="py-4 md:flex-1 md:px-4 md:py-6"
        :data-test-id="`steps-description-row-step-${index + 1}`"
      >
        <span
          class="flex size-8 items-center justify-center rounded-full bg-teal-500 text-sm font-bold text-white"
          aria-hidden="true"
        >
          {{ index + 1 }}
        </span>
        <h3 class="mt-3 text-base font-medium text-gray-900">
          {{ step.title }}
        </h3>
        <div class="mt-1 text-sm leading-[22px] text-stone-500">
          <HtmlReadMore
            :html="step.html"
            :test-id-prefix="`steps-description-row-step-${index + 1}`"
          />
        </div>
      </div>
    </div>
  </div>
  <StrapiContentWrapper v-else-if="!parsed" :rich-text="richText" />
  <div v-else data-test-id="steps-description">
    <div
      v-if="parsed.preambleHtml"
      class="steps-description__preamble mb-4 text-sm font-normal leading-[22px] text-stone-500"
      data-test-id="steps-description-preamble"
      v-html="parsed.preambleHtml"
    />
    <div class="border-t border-b border-gray-200">
      <div class="grid grid-cols-1 md:grid-cols-2">
        <ol
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
          class="m-0 list-none p-0"
          :data-test-id="
            columnIndex === 0
              ? 'steps-description-left'
              : 'steps-description-right'
          "
        >
          <li
            v-for="step in column"
            :key="step.number"
            class="flex items-start gap-3 px-0 py-4 md:px-4"
            :data-test-id="`steps-description-step-${step.number}`"
          >
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-teal-500 text-sm font-bold text-white"
              aria-hidden="true"
            >
              {{ step.number }}
            </span>
            <div
              class="min-w-0 flex-1 self-center text-sm leading-[22px] text-stone-600"
            >
              <HtmlReadMore
                :html="step.html"
                :test-id-prefix="`steps-description-step-${step.number}`"
              />
            </div>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseHtmlList } from '~/utils/parseHtmlList'

interface Step {
  number: number
  html: string
}

interface RowStep {
  title: string
  html: string
}

const {
  richText = '',
  layout = 'columns',
  title,
  subtitle,
  steps = [],
} = defineProps<{
  /** Required when layout is 'columns' (default): CMS rich text containing a <ul>/<ol> of steps. */
  richText?: string
  layout?: 'columns' | 'row'
  /** 'row' layout only: heading shown above the steps. */
  title?: string
  /** 'row' layout only: subheading shown below the title. */
  subtitle?: string
  /** 'row' layout only: one entry per step, each with its own title and description HTML. */
  steps?: RowStep[]
}>()

const parsed = computed(() => parseHtmlList(richText))

const columnSteps = computed<Step[]>(() => {
  if (!parsed.value) {
    return []
  }

  return parsed.value.items.map((html, index) => ({
    number: index + 1,
    html,
  }))
})

const columns = computed(() => {
  if (!columnSteps.value.length) {
    return []
  }

  const midpoint = Math.ceil(columnSteps.value.length / 2)
  const left = columnSteps.value.slice(0, midpoint)
  const right = columnSteps.value.slice(midpoint)

  return right.length > 0 ? [left, right] : [left]
})
</script>

<style scoped lang="postcss">
.steps-description__preamble :deep(p) {
  @apply m-0;
}

.steps-description__preamble :deep(p + p) {
  @apply mt-2 block;
}
</style>
